import * as scoreUtil from "./score/score-util.js";
import * as constants from "./constants.js";
import * as respawn from "./respawn.js";

/**
 * Per-player "fun stats" for the end-of-match screen. computeRoundStats runs on a
 * FINISHED round's live gameState (call it before enterVictoryLap, like scoring),
 * because the replay-trimmed state stored in roundsData drops worms' odometers and
 * the steering segments. aggregateMatchStats folds the per-round snapshots into
 * the match totals the results screen shows.
 *
 * Everything here is plain-JSON bookkeeping: safe to store in roundsData, ship in
 * a server message, or drop in a jsonb column.
 */

// Distance and turn thresholds are cosmetic knobs, not gameplay: a "turn" is any
// steering segment in one direction lasting at least this long (filters out
// keyboard jitter without hiding real flicks).
var MIN_TURN_DURATION = 0.04;

function emptyPlayerStats() {
    return {
        distance: 0,
        turnsLeft: 0,
        turnsRight: 0,
        powerUps: {self: 0, others: 0, all: 0},
        crashesCaused: 0,
        gapsThreaded: 0,
        aliveTime: 0,
        survived: false,
        wonRound: false
    };
}

export function computeRoundStats(gameState) {
    var byPlayer = {};
    gameState.players.forEach(function (player) {
        byPlayer[player.id] = emptyPlayerStats();
    });
    function forPlayer(playerId) {
        if (!byPlayer[playerId]) {
            byPlayer[playerId] = emptyPlayerStats();
        }
        return byPlayer[playerId];
    }

    // trackId -> the worm's CURRENT owner (ownership can move via switcharoonie;
    // pickups and kills credit whoever holds the worm now).
    var ownerByTrackId = {};
    gameState.worms.forEach(function (worm) {
        if (worm.trackId) {
            ownerByTrackId[worm.trackId] = worm.playerId;
        }
        if (worm.gapsThreaded) {
            forPlayer(worm.playerId).gapsThreaded += worm.gapsThreaded;
        }
    });

    // Distance: summed from path segments (per-segment playerId is who owned the
    // worm at the time, so a mid-round owner swap attributes correctly).
    Object.keys(gameState.wormPathSegments).forEach(function (trackId) {
        gameState.wormPathSegments[trackId].forEach(function (seg) {
            if (seg.playerId !== undefined && seg.speed > 0 && seg.duration > 0) {
                forPlayer(seg.playerId).distance += seg.speed * seg.duration;
            }
        });
    });

    // Turns: one per steering segment held in a direction.
    Object.keys(gameState.playerSteeringSegments || {}).forEach(function (playerId) {
        gameState.playerSteeringSegments[playerId].forEach(function (seg) {
            if (seg.duration < MIN_TURN_DURATION) {
                return;
            }
            if (seg.steering === constants.STEERING_LEFT) {
                forPlayer(playerId).turnsLeft += 1;
            } else if (seg.steering === constants.STEERING_RIGHT) {
                forPlayer(playerId).turnsRight += 1;
            }
        });
    });

    // Power-up grabs: despawn.grabbedBy is the grabbing worm's trackId; the
    // matching spawn's powerUp.affects classifies it (self/others/all).
    var affectsByPowerUpId = {};
    gameState.powerUpEvents.forEach(function (event) {
        if (event.type === "spawn") {
            affectsByPowerUpId[event.powerUp.id] = event.powerUp.affects;
        } else if (event.type === "despawn" && event.grabbedBy) {
            var playerId = ownerByTrackId[event.grabbedBy];
            var affects = affectsByPowerUpId[event.id];
            if (playerId !== undefined && affects && forPlayer(playerId).powerUps[affects] !== undefined) {
                forPlayer(playerId).powerUps[affects] += 1;
            }
        }
    });

    // Deaths: survival time per player, and "crashes caused" credited to the
    // painter of the trail a DIFFERENT player died on (killer attribution added
    // to worm_died by core-functions.killWorm).
    var startPhaseTime = gameState.startPhaseTime || 0;
    var roundTime = Math.max(0, gameState.gameTime - startPhaseTime);
    var deathTimeByPlayerId = {};
    gameState.gameEvents.forEach(function (event) {
        if (event.type === "player_died") {
            deathTimeByPlayerId[event.playerId] = event.time;
        } else if (event.type === "worm_died") {
            // (a Respawn round credits the colour of the trail, respawn.js)
            var by = gameState.respawn ? respawn.crashedInto(event) : event.killerPlayerId;
            if (by !== undefined && by !== event.playerId) {forPlayer(by).crashesCaused += 1;}
        }
    });
    // A Respawn round: everyone comes back, so alive is the total, and the
    // longest single life is kept too (respawn.js lives)
    var lives = gameState.respawn ? respawn.lives(gameState) : null;
    Object.keys(byPlayer).forEach(function (playerId) {
        var deathTime = deathTimeByPlayerId[playerId];
        if (lives) {
            var l = lives[playerId] || { total: 0, longest: 0 };
            byPlayer[playerId].aliveTime = l.total;
            byPlayer[playerId].longestLife = l.longest;
            byPlayer[playerId].survived = deathTime === undefined;
            return;
        }
        byPlayer[playerId].aliveTime = deathTime !== undefined
            ? Math.max(0, deathTime - startPhaseTime)
            : roundTime;
        byPlayer[playerId].survived = deathTime === undefined;
    });

    var sorted = scoreUtil.createSortedList(scoreUtil.calculateRoundScore(gameState));
    if (sorted.length > 0) {
        forPlayer(sorted[0].id).wonRound = true;
    }

    return {roundTime: roundTime, byPlayer: byPlayer};
}

export function aggregateMatchStats(roundStatsList) {
    var totals = {};
    var totalRoundTime = 0;
    function forPlayer(playerId) {
        if (!totals[playerId]) {
            totals[playerId] = {
                distance: 0,
                turnsLeft: 0,
                turnsRight: 0,
                powerUps: {self: 0, others: 0, all: 0},
                crashesCaused: 0,
                gapsThreaded: 0,
                aliveTime: 0,
                longestSurvival: 0,
                longestSurvivalRound: 0,
                roundsWon: 0,
                rounds: 0
            };
        }
        return totals[playerId];
    }
    roundStatsList.forEach(function (roundStats, roundIndex) {
        if (!roundStats || !roundStats.byPlayer) {
            return;
        }
        totalRoundTime += roundStats.roundTime || 0;
        Object.keys(roundStats.byPlayer).forEach(function (playerId) {
            var r = roundStats.byPlayer[playerId];
            var t = forPlayer(playerId);
            t.distance += r.distance;
            t.turnsLeft += r.turnsLeft;
            t.turnsRight += r.turnsRight;
            t.powerUps.self += r.powerUps.self;
            t.powerUps.others += r.powerUps.others;
            t.powerUps.all += r.powerUps.all;
            t.crashesCaused += r.crashesCaused;
            t.gapsThreaded += r.gapsThreaded;
            t.aliveTime += r.aliveTime;
            t.rounds += 1;
            if (r.wonRound) {
                t.roundsWon += 1;
            }
            if (r.aliveTime > t.longestSurvival) {
                t.longestSurvival = r.aliveTime;
                t.longestSurvivalRound = roundIndex + 1;
            }
        });
    });
    return {totalRoundTime: totalRoundTime, totalRounds: roundStatsList.length, byPlayer: totals};
}
